import {AsyncPipe, NgFor, NgIf, NgTemplateOutlet} from '@angular/common';
import {A11yModule} from '@angular/cdk/a11y';
import {CdkOverlayOrigin, ConnectedPosition, OverlayModule} from '@angular/cdk/overlay';
import {tap} from 'rxjs';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {RouterLink} from '@angular/router';
import {PageHeaderBreadcrumb, PageHeaderContext, PageHeaderContextService} from './page-header-context.service';

@Component({
    selector: 'app-page-header-context',
    standalone: true,
    imports: [AsyncPipe, NgFor, NgIf, NgTemplateOutlet, RouterLink, OverlayModule, A11yModule],
    templateUrl: './page-header-context.component.html',
    styleUrls: ['./page-header-context.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHeaderContextComponent {
    readonly context$ = inject(PageHeaderContextService).context$.pipe(tap(() => this.closePanel()));
    readonly panelPositions: ConnectedPosition[] = [{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
    }];
    activeOrigin: CdkOverlayOrigin;
    activeCrumb: PageHeaderBreadcrumb | null = null;
    panelOpen = false;
    hoverOpened = false;

    trackCrumb(_index: number, crumb: PageHeaderBreadcrumb): string {
        return crumb.id;
    }

    contractRp(crumb: PageHeaderBreadcrumb): string | undefined {
        return crumb.id === 'contract' ? crumb.details?.find(detail => detail.label === 'RP')?.value : undefined;
    }

    title(crumb: PageHeaderBreadcrumb): string {
        return {
            kpi: 'KPI',
            contractor: 'Compañía',
            provider: 'Proveedor',
            contract: 'Contrato',
            period: 'Período'
        }[crumb.id];
    }

    openPanel(crumb: PageHeaderBreadcrumb | null, origin: CdkOverlayOrigin, hover = false): void {
        if (crumb?.id === 'period') return;
        if (!hover && this.panelOpen && this.activeOrigin === origin) {
            this.closePanel();
            return;
        }
        this.activeCrumb = crumb;
        this.activeOrigin = origin;
        this.hoverOpened = hover;
        this.panelOpen = true;
    }

    hoverDetails(crumb: PageHeaderBreadcrumb, origin: CdkOverlayOrigin): void {
        const view = origin.elementRef.nativeElement.ownerDocument.defaultView;
        if (!this.panelOpen && crumb.id !== 'kpi' && crumb.id !== 'period' && view?.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            this.openPanel(crumb, origin, true);
        }
    }

    leaveOrigin(event: MouseEvent): void {
        const next = event.relatedTarget as Element | null;
        if (this.hoverOpened && !next?.closest?.('.breadcrumb-panel')) this.closePanel();
    }

    leavePanel(event: MouseEvent): void {
        const next = event.relatedTarget as Node | null;
        if (this.hoverOpened && !this.activeOrigin?.elementRef.nativeElement.contains(next)) this.closePanel();
    }

    panelCrumbs(context: PageHeaderContext): ReadonlyArray<PageHeaderBreadcrumb> {
        return this.activeCrumb ? [this.activeCrumb] : (context.breadcrumbs ?? []).filter(crumb => crumb.id !== 'period');
    }

    closePanel(): void {
        this.panelOpen = false;
        this.activeCrumb = null;
        this.hoverOpened = false;
    }

    panelKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.closePanel();
        }
    }
}
